/* eslint-disable camelcase */
import { memo } from 'react';

import type { MRT_TableInstance as MRTTableInstance, MRT_ColumnDef } from 'material-react-table';
import {
  MRT_GlobalFilterTextField as MRTGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
} from 'material-react-table';

import { Box, IconButton, Switch } from '@/ui';
import ExportButton from './export-button';
import JSONExportButton from './json-export-button';
import FilterMap from './filter-map';
import type { ColumnsType } from './data-table-types';
import type { TypeFeatureInfoEntry } from '@/api/types/map-schema-types';
import type { SxStyles } from '@/ui/style/types';
import { ClearFiltersIcon } from '@/ui/icons';
import { logger } from '@/core/utils/logger';

/** Properties for the TopToolbar component. */
// GV: Disabled prop-types for this to work. From the react 19 ugprade guide it seems that prop-types are deprecated.
interface TopToolbarProps<TData extends ColumnsType> {
  /** Classes or styles for the component. */
  sxClasses: SxStyles;

  /** Settings for the datatable, indexed by layerPath. */
  datatableSettings: Record<string, { toolbarRowSelectedMessageRecord: string }>;

  /** The path for the current layer being processed. */
  layerPath: string;

  /** Translation function for internationalization. */
  t: (key: string) => string;

  /** The current global filter value. */
  globalFilter: string | null;

  /** Utility functions provided by the table instance. */
  useTable: {
    resetColumnFilters: () => void;
    getFilteredRowModel: () => { rows: Array<{ original: TData }> };
  } | null;

  /** Column definitions for the table. */
  columns: MRT_ColumnDef<ColumnsType, unknown>[];

  /** The data object containing features for the table. */
  data: {
    features?: TypeFeatureInfoEntry[] | null;
  };

  /** The Material React Table instance. */
  table: MRTTableInstance<ColumnsType>;
}

/**
 * Renders the top toolbar for the data table with filters, search, and export controls.
 *
 * @param props - TopToolbar properties
 * @returns The toolbar element
 */
function TopToolbar(props: TopToolbarProps<ColumnsType>): JSX.Element {
  // Log
  logger.logTraceRender('components/data-table/top-toolbar');

  const { sxClasses, datatableSettings, layerPath, t, globalFilter, useTable, columns, data, table } = props;
  return (
    <Box
      className="data-table-top-toolbar"
      role="region"
      display="flex"
      aria-label={t('dataTable.tableControls')}
      sx={{ justifyContent: 'space-between', borderBottom: '1px solid #9e9e9e' }}
      p={4}
    >
      <Box display="flex" sx={{ flexDirection: 'column', justifyContent: 'space-evenly' }}>
        <Box component="p" role="status" className="filter-results-summary" sx={sxClasses.selectedRows} aria-live="polite">
          {datatableSettings[layerPath].toolbarRowSelectedMessageRecord}
        </Box>
        <Box display="flex">
          <FilterMap layerPath={layerPath} isGlobalFilterOn={!!globalFilter?.length} />
        </Box>
      </Box>
      <Box display="flex" sx={{ flexDirection: 'column' }}>
        <Box sx={{ float: 'right', marginLeft: 'auto', maxWidth: '15rem' }}>
          <form role="search" onSubmit={(e) => e.preventDefault()} aria-label={t('dataTable.searchInputLabel')}>
            <MRTGlobalFilterTextField className="buttonOutline" table={table} />
          </form>
        </Box>
        <Box display="flex" sx={{ justifyContent: 'space-around' }}>
          <Switch
            checked={table.getState().showColumnFilters}
            onChange={() => table.setShowColumnFilters(!table.getState().showColumnFilters)}
            size="small"
            label={
              table.getState().showColumnFilters
                ? `${t('general.hide')} ${t('dataTable.filterToggle')}`
                : `${t('general.show')} ${t('dataTable.filterToggle')}`
            }
            sx={sxClasses.filterMap}
          />

          <IconButton
            className="buttonOutline"
            aria-label={t('dataTable.clearFilters')}
            color="primary"
            onClick={() => useTable?.resetColumnFilters()}
          >
            <ClearFiltersIcon />
          </IconButton>

          {/* Override column pinning options */}
          <MRTShowHideColumnsButton
            className="buttonOutline"
            table={{ ...table, options: { ...table.options, enableColumnPinning: false } }}
          />
          <MRTToggleDensePaddingButton className="buttonOutline" table={table} />

          {/* Export Buttons */}
          {useTable?.getFilteredRowModel()?.rows ? (
            <ExportButton layerPath={layerPath} rows={useTable.getFilteredRowModel().rows.map((row) => row.original)} columns={columns}>
              <JSONExportButton
                rows={useTable.getFilteredRowModel().rows.map((row) => row.original)}
                features={data.features as TypeFeatureInfoEntry[]}
                layerPath={layerPath}
              />
            </ExportButton>
          ) : undefined}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(TopToolbar);
